import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { Toaster, toast } from "sonner";

// Pages
import AuthPage from "./pages/AuthPage";
import CandidateDashboard from "./pages/CandidateDashboard";
import ExamInterface from "./pages/ExamInterface";
import ExamResult from "./pages/ExamResult";
import AdminDashboard from "./pages/AdminDashboard";
import QuestionBank from "./pages/QuestionBank";
import ExamManagement from "./pages/ExamManagement";
import ResultsAnalytics from "./pages/ResultsAnalytics";
import IPLogs from "./pages/IPLogs";
import FeedbackPage from "./pages/FeedbackPage";
import OrganiserDashboard from "./pages/OrganiserDashboard";
import OrganiserManagement from "./pages/OrganiserManagement";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios interceptor for automatic token expiry handling
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      if (window.location.pathname !== "/auth") {
        window.location.href = "/auth";
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (email, password, captchaToken) => {
    const payload = { email, password };
    if (captchaToken) payload.captcha_token = captchaToken;
    const response = await axios.post(`${API}/auth/login`, payload);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (fullName, email, password, mobile) => {
    const response = await axios.post(`${API}/auth/register`, {
      full_name: fullName,
      email,
      password,
      mobile: mobile || null
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      const t = localStorage.getItem("token");
      if (t) {
        await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${t}` } }).catch(() => {});
      }
    } finally {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to={user.role === "admin" ? "/admin" : user.role === "organiser" ? "/organiser" : "/dashboard"} replace />;
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          {/* Public Routes */}
          <Route path="/auth" element={<AuthPage />} />
          
          {/* Candidate Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute requiredRole="candidate">
              <CandidateDashboard />
            </ProtectedRoute>
          } />
          <Route path="/exam/:examId" element={
            <ProtectedRoute requiredRole="candidate">
              <ExamInterface />
            </ProtectedRoute>
          } />
          <Route path="/exam/:examId/result" element={
            <ProtectedRoute requiredRole="candidate">
              <ExamResult />
            </ProtectedRoute>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute requiredRole="admin">
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/questions" element={
            <ProtectedRoute requiredRole="admin">
              <QuestionBank />
            </ProtectedRoute>
          } />
          <Route path="/admin/exams" element={
            <ProtectedRoute requiredRole="admin">
              <ExamManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/results" element={
            <ProtectedRoute requiredRole="admin">
              <ResultsAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/admin/logs" element={
            <ProtectedRoute requiredRole="admin">
              <IPLogs />
            </ProtectedRoute>
          } />
          <Route path="/admin/feedback" element={
            <ProtectedRoute requiredRole="admin">
              <FeedbackPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/organisers" element={
            <ProtectedRoute requiredRole="admin">
              <OrganiserManagement />
            </ProtectedRoute>
          } />

          {/* Organiser Routes */}
          <Route path="/organiser" element={
            <ProtectedRoute requiredRole="organiser">
              <OrganiserDashboard />
            </ProtectedRoute>
          } />
          
          {/* Default Redirect */}
          <Route path="/" element={<Navigate to="/auth" replace />} />
          <Route path="*" element={<Navigate to="/auth" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
